import './DesignerWorkspace.css';
import PropTypes from 'prop-types';
import { AgTable, Dialog, Icon, Loading, Menu } from '@icp/components';
import { useTranslation } from 'react-i18next';
import { forwardRef, useMemo, useRef, useState } from 'react';
import { useEventCallback, useForkRef } from '@icp/hooks';
import { Checkbox, Modal } from 'antd';
import { restApi } from '@icp/settings';
import { get, merge } from 'lodash-es';
import {
  compressToTranslate,
  deCompressToTranslate,
  getAllNeedTranslateRowData,
  getAutoTranslateChange,
  getToTranslateRanges,
} from '../utils';

const I18nTable = forwardRef(function I18nTable(props, ref) {
  const {
    className,
    style,
    tableData,
    originalI18nResources,
    noDefaultValue,
    toolbarChildren,
    readonly,
    allowedLanguages,
    actionItems,
    onActionClick,
    onResourceChange,
    onCellValueChanged,
    AgTableProps,
  } = props;

  const { t, i18n } = useTranslation(['default']);

  const [menuAnchorEl, setMenuAnchorEl] = useState(null);
  const [progress, setProgress] = useState(null);
  const [showProgress, setShowProgress] = useState(false);

  const gridRef = useRef(null);
  const handleRef = useForkRef(gridRef, ref);
  const forceReplaceRef = useRef();
  const progressTimeout = useRef(null); // 如果超过 3s 没有成功，则显示进度 dialog

  const controller = useRef(null);

  const doAutoTranslate = async (lngs, originalData, forceReplace) => {
    controller.current = new AbortController();
    const { signal } = controller.current;

    progressTimeout.current = setTimeout(() => {
      setShowProgress(true);
    }, 3000);
    gridRef.current.node.querySelector('.ag-root-wrapper').style = 'cursor: progress';

    const requestBySlice = async () => {
      // 分片请求，防止 ai 接口太慢卡死一直返回结果
      const perCount = 100;
      const slices = Math.ceil(originalData.length / perCount);
      const allResults = [];

      for (let index = 0; index < slices; index++) {
        setProgress({
          count: Math.min(index * perCount + perCount, originalData.length),
          total: originalData.length,
        });
        const sliceData = originalData.slice(index * perCount, index * perCount + perCount);
        const toTranslate = compressToTranslate(sliceData, allowedLanguages, forceReplace);
        // eslint-disable-next-line no-await-in-loop
        await Promise.all(
          lngs.map((lng) => {
            if (!Object.keys(toTranslate).length) {
              return Promise.resolve({});
            }
            return restApi
              .post(
                `/aip/api/gpt/translate-i18n?toLanguage=${lng}`,
                {
                  content: toTranslate,
                },
                { signal },
              )
              .then(({ content }) => {
                return content;
              });
          }),
        ).then((restArray) => {
          const resByLng = {};
          lngs.forEach((lng, i) => {
            resByLng[lng] = restArray[i];
          });

          const change = getAutoTranslateChange({
            toTranslate,
            resByLng,
            originalData: sliceData,
            lngs,
            allowedLanguages,
            forceReplace,
          });

          const newData = deCompressToTranslate({
            toTranslate,
            resByLng,
            originalData: sliceData,
            lngs,
            allowedLanguages,
            forceReplace,
          });
          allResults.push({ change, newData });
        });
      }

      return allResults;
    };

    return requestBySlice()
      .then((sliceRes) => {
        let allChange = {};
        let allNewData = [];
        for (const lngRes of sliceRes) {
          const { change, newData } = lngRes;
          allChange = merge(allChange, change);
          allNewData = allNewData.concat(newData);
        }
        return { allChange, allNewData };
      })
      .then(({ allChange, allNewData }) => {
        gridRef.current.api.applyTransaction({ update: allNewData });
        onResourceChange(allChange);
      })
      .finally(() => {
        gridRef.current.node.querySelector('.ag-root-wrapper').style = null;
        clearTimeout(progressTimeout.current);
        setProgress(null);
        setShowProgress(false);
      });
  };

  const cancelTranslate = () => {
    controller.current?.abort();
    gridRef.current.node.querySelector('.ag-root-wrapper').style = null;
    clearTimeout(progressTimeout.current);
  };

  const handleAutoTranslate = useEventCallback((key) => {
    const lngs = key === 'all' ? allowedLanguages : [key];
    const label =
      key === 'all'
        ? t('i18n.auto-translate.all-lng')
        : new Intl.DisplayNames([i18n.language], {
            type: 'language',
            languageDisplay: 'standard',
          }).of(key);

    Modal.confirm({
      title: t('i18n.auto-translate.label-lng', { name: label }),
      centered: true,
      closable: false,
      content: (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
          <div>
            <Checkbox defaultChecked={false} ref={forceReplaceRef}>
              {t('i18n.auto-translate.forceReplace')}
            </Checkbox>
          </div>
        </div>
      ),
      onOk: async () => {
        const originalData = getAllNeedTranslateRowData(gridRef);
        const forceReplace = forceReplaceRef.current.input.checked;
        doAutoTranslate(lngs, originalData, forceReplace);
      },
      onCancel: cancelTranslate,
    });
  });

  const handleMenuClick = ({ key }) => {
    if (key.startsWith('autoTranslate')) {
      handleAutoTranslate(key.split('autoTranslate.')[1]);
    } else {
      onActionClick({ key });
    }
  };

  const getContextMenuItems = (params) => {
    if (!params.node) {
      return params.defaultItems;
    }

    const toTranslateRanges = getToTranslateRanges(params, allowedLanguages);

    return [
      toTranslateRanges?.length
        ? {
            name: t('i18n.auto-translate.label'),
            icon: '',
            action: () => {
              toTranslateRanges.forEach((item) => {
                doAutoTranslate(item.lngs, item.originalData, true);
              });
            },
          }
        : null,
      'copy',
      'separator',
      'export',
    ].filter(Boolean);
  };

  const defaultColDef = useMemo(() => {
    return {
      wrapText: true,
      autoHeight: true,
      editable: (params) => {
        const cellValue = params.data ? params.data[params.colDef.colId] : null;
        return (
          !readonly &&
          !params.data.key?.startsWith('standard|>') &&
          // 如果是 object 的话意味着在 json 模式下手动写的嵌套对象，table 里无法编辑，需要切去 json 模式修改
          (!cellValue || typeof cellValue === 'number' || typeof cellValue === 'string')
        );
      },
      mainMenuItems: (params) => {
        return [
          allowedLanguages.includes(params.column.colId)
            ? {
                name: t('i18n.auto-translate.column-label'),
                icon: '',
                action: () => {
                  handleAutoTranslate(params.column.colId);
                },
              }
            : null,
          'sortAscending',
          'sortDescending',
          'separator',
          'autoSizeThis',
          'autoSizeAll',
        ].filter(Boolean);
      },
    };
  }, [allowedLanguages, handleAutoTranslate, readonly, t]);

  const columnDefs = useMemo(() => {
    return [
      {
        colId: 'key',
        field: 'key',
        headerName: 'Key',
        width: 280,
        sort: 'asc',
      },
    ]
      .concat(
        allowedLanguages.map((lng) => {
          return {
            colId: lng,
            field: lng,
            headerName: new Intl.DisplayNames([i18n.language], {
              type: 'language',
              languageDisplay: 'standard',
            }).of(lng),
            cellClass: (params) => {
              const { value, colDef, data } = params;
              const old = get(originalI18nResources, [colDef.colId, data.namespace, data.key]);
              if (value !== old) {
                return 'modified';
              }
              return undefined;
            },
          };
        }),
      )
      .concat([
        !noDefaultValue
          ? {
              colId: 'defaultValue',
              field: 'defaultValue',
              headerName: t('defaultValue'),
              editable: false,
            }
          : null,
        {
          colId: 'namespace',
          field: 'namespace',
          headerName: 'Namespace',
        },
      ])
      .filter(Boolean);
  }, [allowedLanguages, i18n.language, originalI18nResources, noDefaultValue, t]);

  return (
    <div className={className} style={{ position: 'relative', ...style }}>
      <div
        style={{
          position: 'absolute',
          left: 8,
          top: 6,
          zIndex: 1,
          display: 'flex',
          alignItems: 'center',
          gap: 8,
          height: 32,
        }}
      >
        {toolbarChildren}
        {!readonly ? (
          <button
            className="icp-action-button"
            onClick={(event) => setMenuAnchorEl(event.currentTarget)}
          >
            <Icon name="solar:menu-dots-circle-linear" />
            {t('action', { ns: 'default' })}
            <Icon name="oct:chevron-down" size={12} />
          </button>
        ) : null}
        <Menu
          placement="bottom-start"
          open={!!menuAnchorEl}
          anchorEl={menuAnchorEl}
          onClose={() => setMenuAnchorEl(null)}
          items={actionItems.concat([
            {
              key: 'autoTranslate',
              label: t('i18n.auto-translate.label'),
              children: [
                { key: 'autoTranslate.all', label: t('i18n.auto-translate.all-lng') },
              ].concat(
                allowedLanguages.map((lng) => {
                  return {
                    key: `autoTranslate.${lng}`,
                    label: new Intl.DisplayNames([i18n.language], {
                      type: 'language',
                      languageDisplay: 'standard',
                    }).of(lng),
                  };
                }),
              ),
            },
          ])}
          onClick={handleMenuClick}
        />
      </div>
      <AgTable
        withCard={true}
        className="fd-i18n-table"
        defaultColDef={defaultColDef}
        columnDefs={columnDefs}
        rowData={tableData}
        rowModelType="clientSide"
        tableSize="small"
        rowSelection={{
          mode: 'multiRow',
          checkboxes: false,
          headerCheckbox: false,
        }}
        cellSelection={true}
        enableCellTextSelection={false}
        suppressModelUpdateAfterUpdateTransaction={true}
        suppressContextMenu={false}
        fuzzySearchOpen={true}
        suppressAddButton={true}
        suppressDeleteButton={true}
        suppressFuzzySearchSpeech={true}
        suppressColumnSelect={true}
        suppressToolbarRefresh={true}
        suppressTableSetting={true}
        suppressFullscreen={true}
        animateRows={false}
        onCellValueChanged={onCellValueChanged}
        getContextMenuItems={getContextMenuItems}
        {...AgTableProps}
        ref={handleRef}
      />
      <Dialog
        open={showProgress && !!progress}
        title={t('i18n.auto-translate.label')}
        disableOkButton={true}
        disableEscapeKeyDown={true}
        disableBackdropClose={true}
        cancelText={t('i18n.stop')}
        onClose={() => {
          cancelTranslate();
          setProgress(null);
        }}
      >
        {progress
          ? `${t('i18n.auto-translate.processing')} ${progress.count} / ${progress.total}`
          : null}
        <Loading centered={false} style={{ marginLeft: 8 }} />
      </Dialog>
    </div>
  );
});

I18nTable.propTypes = {
  className: PropTypes.string,
  tableData: PropTypes.arrayOf(PropTypes.shape({})),
  originalI18nResources: PropTypes.shape({}),
  noDefaultValue: PropTypes.bool,
  toolbarChildren: PropTypes.node,
  readonly: PropTypes.bool,
  allowedLanguages: PropTypes.arrayOf(PropTypes.string),
  actionItems: PropTypes.arrayOf(PropTypes.shape({})),
  onActionClick: PropTypes.func,
  onResourceChange: PropTypes.func,
  onCellValueChanged: PropTypes.func,
  AgTableProps: PropTypes.shape({}),
};

export default I18nTable;
